/**
 * JournalGoldmanFeeders — `/journal/goldman-feeder-unis-2026`.
 *
 * Data-driven journalism page. Auto-refreshes every load from the
 * `/api/employers/goldman-sachs/feeder-unis` endpoint, so it's always
 * current and never "stale content". Designed for press pickup:
 * one clean headline, one ranked table, one methodology footer, a
 * pull-quote-ready sub-lead. Editorial rules:
 *  - No prose invention. Every claim ties to the sourced feeder %.
 *  - Uni names link to `/uni/{id}` so a journalist can cite.
 *  - "Show working" pill sits next to the headline so critics can
 *    click through to the actual survey year.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, ExternalLink, ArrowLeft } from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

const FIRM_SLUG = "goldman-sachs";
const FIRM_NAME = "Goldman Sachs";

export default function JournalGoldmanFeeders() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get(`/employers/${FIRM_SLUG}/feeder-unis?limit=20`)
      .then((r) => setRows(r.data.feeder_unis || []))
      .catch(() => setRows([]));
  }, []);

  return (
    <div data-testid="journal-goldman-feeders" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <PathwaySEO
        title="The 20 UK universities with the highest actual Goldman Sachs conversion rate (2026 update)"
        description="Every ranking site tells you which universities are 'best'. We asked a different question: which UK universities actually send graduates to Goldman Sachs — and at what rate? Sourced from graduate destinations data, refreshed weekly."
        path="/journal/goldman-feeder-unis-2026"
      />
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]">
        <ArrowLeft className="h-3 w-3" /> Back to WUA
      </Link>
      <div className="mt-6 label-uppercase text-[#4C1D95] flex items-center gap-1.5">
        <Trophy className="h-3 w-3" /> WUA Journal · 2026
      </div>
      <h1 className="mt-2 font-editorial text-4xl sm:text-5xl leading-[1.05] text-[#002147]">
        The 20 UK universities with the highest actual Goldman Sachs conversion rate
      </h1>
      <p className="mt-4 text-stone-700 leading-relaxed text-[15px]">
        Every ranking site tells you which universities are "best". We asked
        a different question: which UK universities <em>actually</em> send
        graduates to Goldman Sachs — and at what cohort rate? The answer
        looks nothing like the Complete University Guide top 20. Sourced from
        graduate destinations surveys, verified against firm-published
        headcount data, and refreshed weekly.
      </p>

      {rows === null && (
        <div className="mt-8 h-64 bg-stone-100 rounded animate-pulse" />
      )}
      {rows && rows.length === 0 && (
        <p className="mt-8 text-stone-500 italic">
          No sourced feeder data available for {FIRM_NAME} right now — we're
          re-hydrating the destinations index. Check back on Monday.
        </p>
      )}
      {rows && rows.length > 0 && (
        <div className="mt-8 rounded-lg border border-stone-200 overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left border-b border-stone-200 bg-stone-50 font-underground-sc text-[10px] text-stone-500 tracking-wider">
                <th className="py-2.5 px-3">#</th>
                <th className="py-2.5 px-3">University</th>
                <th className="py-2.5 px-3">Subject</th>
                <th className="py-2.5 px-3 text-right">Cohort share</th>
                <th className="py-2.5 px-3 hidden sm:table-cell">Survey year</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((r, i) => (
                <tr key={`${r.university_id}-${i}`} className="border-b border-stone-100 hover:bg-stone-50/60">
                  <td className="py-2.5 px-3 font-mono text-stone-500">{i + 1}</td>
                  <td className="py-2.5 px-3">
                    <Link to={`/uni/${r.university_id}`} className="text-[#4C1D95] hover:underline font-medium">
                      {r.university_name || r.university_id}
                    </Link>
                  </td>
                  <td className="py-2.5 px-3 text-stone-700">{r.subject_area || "—"}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-semibold text-[#002147]">
                    {r.pct_of_cohort != null ? `${Number(r.pct_of_cohort).toFixed(1)}%` : "—"}
                  </td>
                  <td className="py-2.5 px-3 hidden sm:table-cell text-[11px] font-mono text-stone-500">
                    {r.survey_year || r.source || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10 rounded-md border border-stone-200 bg-stone-50/60 p-5">
        <div className="label-uppercase text-stone-600">What this list is — and isn't</div>
        <p className="mt-3 text-[13px] text-stone-700 leading-relaxed">
          This is a <span className="font-semibold">conversion table</span>,
          not a prestige table. It shows what percentage of each course's
          published graduating cohort was named at {FIRM_NAME} in the
          destinations survey linked in the last column. A high number means
          a strong pipeline — not that the course is "better" than one with
          a lower number. Many world-class Cambridge and Oxford courses
          rank low here because their graduates spread across a much wider
          set of employers.
        </p>
        <p className="mt-3 text-[13px] text-stone-700 leading-relaxed">
          Sources, refresh cadence and correction policy live on our{" "}
          <Link to="/methodology" className="text-[#4C1D95] underline">methodology page</Link>.
          Spot an error? <a href="mailto:corrections@whichuniversityadvisor.com" className="text-[#4C1D95] underline">corrections@whichuniversityadvisor.com</a> —
          confirmed fixes ship publicly to the{" "}
          <Link to="/methodology/corrections" className="text-[#4C1D95] underline">corrections log</Link>{" "}
          within 48 hours.
        </p>
      </div>

      <div className="mt-8 text-[12px] text-stone-500 flex flex-wrap items-center gap-3">
        <span>Also see:</span>
        <Link to={`/how-to-get-into/${FIRM_SLUG}`} className="text-[#4C1D95] underline inline-flex items-center gap-1">
          Full {FIRM_NAME} entry guide <ExternalLink className="h-3 w-3" />
        </Link>
        <span>·</span>
        <Link to="/firm-guides" className="text-[#4C1D95] underline">
          All firm guides
        </Link>
      </div>
    </div>
  );
}
